import { observer } from 'mobx-react-lite'
import { Box, Divider, EnumHelperText, HelperText, Switch, Typography } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { MessageField } from 'widgets/MessageField'
import { useInboxLayerContext } from 'pages/settings/pages/inboxes/context/inboxLayerContext'
import { BusinessHoursTitle } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/businessHours/ui/BusinessHoursTitle/BusinessHoursTitle'
import { ScheduleTimePicker } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/businessHours/ui/ScheduleTimePicker/ScheduleTimePicker'
import { CustomDayRow } from 'pages/settings/pages/inboxes/pages/id/pages/settings/messaging/businessHours/ui/CustomDayRow/CustomDayRow'
import styles from './styles.module.scss'

export const BusinessHours = observer(() => {
  const {
    inboxSettingsStore: { businessHoursStore },
  } = useInboxLayerContext()
  const {
    isActive,
    toggleIsActive,
    timezonesList,
    timezone,
    setTimezone,
    messageFieldStore,
    messageFieldError,
    scheduleType,
    selectedScheduleType,
    setSelectedScheduleType,
    getEveryDayTime,
    setEveryDayTime,
    minTime,
    maxTime,
    days,
    getDayTime,
    setDayTime,
    toggleDayClosed,
    getDayLabel,
  } = businessHoursStore

  return (
    <div className={styles.wrap}>
      <Box display={'flex'} alignItems={'center'}>
        <Box display={'flex'} flexDirection={'column'} gap={'4px'} flex={1}>
          <Typography
            ariaLabel='title'
            variant='body-lg-medium'
            color='var(--content-primary-primary)'
          >
            Business hours
          </Typography>
          <Typography
            ariaLabel='title'
            variant='body-md-regular'
            color='var(--content-primary-tertiary)'
          >
            Set business hours to this inbox
          </Typography>
        </Box>
        <div className={styles.switcherWrap}>
          <Switch value={isActive} onChange={toggleIsActive} size='middle' />
        </div>
      </Box>
      {isActive && (
        <>
          <Box display={'flex'} alignItems={'center'} marginTop={'48px'}>
            <BusinessHoursTitle
              title='Timezone'
              description='Select the timezone your business operates in'
            />
            <SelectField
              value={timezone?.value}
              items={timezonesList}
              setValue={(_, item) => setTimezone(item)}
              withSearch={false}
              dropdownProps={{
                propsDropdownList: {
                  withSearch: true,
                  withGrouping: true,
                  dropdownGroupProps: {
                    isScroll: false,
                  },
                },
              }}
              btnProps={{
                fullWidth: true,
                size: 'large',
                style: {
                  height: '40px',
                  width: '220px',
                },
                text: timezone ? (
                  <Box display={'flex'}>
                    <Typography
                      variant={'text-input-field-r'}
                      ariaLabel={'selectedTimezone'}
                      ellipsis
                      tooltipProps={{
                        title: timezone.label,
                        placement: 'top',
                      }}
                    >
                      {timezone.label}
                    </Typography>
                  </Box>
                ) : null,
              }}
            />
          </Box>
          <Divider style={{ margin: '24px 0' }} />
          <BusinessHoursTitle
            title='Set business hours'
            description='Send an out-of-office reply to inbound messages outside business hours'
          />
          <Box display={'flex'} marginTop={'16px'} justifyContent={'space-between'}>
            <SelectField
              value={selectedScheduleType}
              setValue={setSelectedScheduleType}
              items={scheduleType}
              withSearch={false}
              btnProps={{
                size: 'large',
                style: {
                  height: '40px',
                  width: '160px',
                },
              }}
            />
            {selectedScheduleType === 'every_day' && (
              <Box display={'flex'} alignItems={'center'} gap={'16px'}>
                <ScheduleTimePicker
                  field='start'
                  setEveryDayTime={setEveryDayTime}
                  getEveryDayTime={getEveryDayTime}
                  minTime={minTime}
                  maxTime={maxTime}
                />
                <Typography
                  variant='body-md-regular'
                  ariaLabel='toTimeLabel'
                  color='var(--content-primary-tertiary)'
                >
                  to
                </Typography>
                <ScheduleTimePicker
                  field='end'
                  setEveryDayTime={setEveryDayTime}
                  getEveryDayTime={getEveryDayTime}
                  minTime={minTime}
                  maxTime={maxTime}
                />
              </Box>
            )}
          </Box>
          {selectedScheduleType === 'custom' && (
            <Box display={'flex'} flexDirection={'column'} gap={'12px'} marginTop={'24px'}>
              {days.map((day) => (
                <CustomDayRow
                  key={day.day}
                  dayKey={day.day}
                  dayLabel={getDayLabel(day.day)}
                  isClosed={day.closed}
                  onToggleClosed={() => toggleDayClosed(day.day)}
                  getDayTime={getDayTime}
                  setDayTime={setDayTime}
                  minTime={minTime}
                  maxTime={maxTime}
                />
              ))}
            </Box>
          )}
          <Divider style={{ margin: '24px 0' }} />
          <Box display={'flex'}>
            <BusinessHoursTitle
              title='Out of office message'
              description='This message will be sent to contacts who reach out outside your business hours.'
            />
            <Box display={'flex'} flexDirection={'column'} className={styles.messageFieldWrap}>
              <MessageField messageFieldStore={messageFieldStore} />
              {messageFieldError && (
                <div className={styles.error}>
                  <HelperText
                    icon={'alertTriangleSmall'}
                    variant={EnumHelperText.Error}
                    text={messageFieldError}
                  />
                </div>
              )}
            </Box>
          </Box>
        </>
      )}
    </div>
  )
})
